import './widgetForm.less';

import {Menu} from "antd"
import React, {useCallback, useMemo} from "react";
import {CaretLeftOutlined, CaretRightOutlined, SearchOutlined} from '@ant-design/icons'

import {linkObservable} from "core/react/links";
import {Section} from "controls/react/layout/section";
import {ValidationSummary} from "controls/react/validationSummary";
import {getWidgetsMenuStructure, generateMenuItems} from "controls/designer/features/widgets/widgetsMenu";
import {loadDesignerSettings, updateDesignerSetting} from "controls/designer/shared";
import {AntModal} from "controls/react/ant/antModal";
import {Toolbar} from "controls/react/layout/toolbar";
import {AntButton} from "controls/react/ant/antButton";
import {AntInput} from "controls/react/ant/antInput";
import {RedirectConfigEditor} from "controls/designer/features/redirectOnClick/redirectConfigEditor";
import {observer} from "mobx-react";
import {MobxManager} from "framework/mobx-integration";
import {ApplicationState} from "framework/applicationState";
import {toJS} from "mobx";
import {newGuid} from "tools/guid";

const i18n = require('core/localization/localization').translator({
  "widget-description-service-preview": {
    "en": " Widget displays the preview of a service model with its states.",
    "no": "Modulen viser en tjenestemodell og dens tilstander"
  },
  "widget-description-application-preview": {
    "en": "Widget displays an application model.",
    "no": "Modulen viser en applikasjonsmodell"
  },
  "widget-description-application-state": {
    "en": "Widget displays aggregated application states for an account and subaccounts. Filtering with tags is available.",
    "no": "Modulen viser de samlede applikasjonsstatusene for en konto og underkontoer. Filtrering med etiketter er tilgjengelig"
  },
  "widget-description-dashboard": {
    "en": "Widget displays a live dashboard data.",
    "no": "Modulen viser et live dashbord i modulen"
  },
  "widget-description-service-summary": {
    "en": "Widget displays service elements and servicequalifiers in a list format with its states. Multiple display settings are available.",
    "no": "Modulen viser tjenesteelementer og tjenestepunkt en listeform med tilstander. Ulike innstillinger er tilgjengelige"
  },
  "widget-description-service-history": {
    "en": "Widget displays a timeline with a service state history. Multiple display settings are available.",
    "no": "Komponenten viser en tidslinje med en status for tjenesten. Ulike innstillinger er tilgjengelige"
  },
  "widget-description-service-selective": {
    "en": "Widget displays a selection of service states. Filtering, sorting and presentation options are available.",
    "no": "Komponenten viser et utvalg av tjenestetilstander. Forskjellige filtrering, sortering og presentasjons muligheter er tilgjengelig"
  },
  "widget-description-service-grid": {
    "en": "Widget displays service grid. Filtering with tags and selection of columns to display are available.",
    "no": "Komponenten viser tjenestetabellen. Filtrering med etiketter og valg av hvilke kolonner som skal vises er mulig"
  },
  "widget-description-service-state": {
    "en": "Widget displays the aggregated service states for an account and subaccounts. Filtering with tags is available.",
    "no": "Komponenten viser de aggregerte tjenestetilstander for en konto og subkontoer. Filtrering med etiketter er mulig"
  },
  "widget-description-service-single-graph": {
    "en": "Widget displays a servicequalifier metric. Multiple display options are available.",
    "no": "Komponenten viser en dataserie for et tjenestepunkt. Det er mulig å velge forskjellige grafer og tidsintervaller"
  },
  "widget-description-service-multi-graph": {
    "en": "Widget displays multiple servicequalifier metrics. Multiple display options are available.",
    "no": "Komponenten viser flere dataserier fra tjenestepunkter. Det er mulig å velge forskjellige grafer og tidsintervaller"
  },
  "widget-description-service-gauge": {
    "en": "Widget displays a gauge with the newest metric for a servicequalifier. You can set minimum, maximum and breach values.",
    "no": "Komponenten viser en måler av siste metrikk serie for et tjenestepunkt. Du kan sette egne verdier for minimum, maksimum og  bruddverdi"
  },
  "widget-description-metric-single-graph": {
    "en": " Widget displays a metric graph. Multiple display options are available.",
    "no": "Komponenten viser en dataserie. Det er mulig å velge forskjellige grafer og tidsintervaller"
  },
  "widget-description-metric-multi-graph-asset": {
    "en": "Widget displays metric series for assets. Multiple display options are available.",
    "no": "Komponenten viser multiple dataserie. Det er mulig å velge forskjellige grafer og tidsintervaller"
  },
  "widget-description-metric-multi-graph-asset-group": {
    "en": "Widget displays metric series for assetgroups. Filtering on metric categories and multiple display options are available.",
    "no": "Komponenten viser metrikker fra ressursegrupper. Det er mulig å filtre på metrikk kategorier, og man kan velge forskjellige grafer og tidsintervaller"
  },
  "widget-description-metric-gauge": {
    "en": "Widget displays a gauge with the newest metric. You can set minimum, maximum and breach values.",
    "no": "Komponenten viser en måler med siste metrikk. Du kan sette egne verdier for minimum, maksimum og  bruddverdi"
  },
  "widget-description-metric-barchart": {
    "en": "Widget displays a bar with the newest metric. You can set minimum, maximum and breach values.",
    "no": "Komponenten viser en måler med siste metrikk. Du kan sette egne verdier for minimum, maksimum og  bruddverdi"
  },
  "widget-description-metric-table": {
    "en": "Widget displays selected metric in a table format. Multiple time settings options are available.",
    "no": "Widgeten viser en metrikk serie i tabellformat. Ulike alternativer for tidsinnstillinger er tilgjengelige"
  },
  "widget-description-metric-stacked-chart": {
    "en": "Widget displays single graphs stacked above each other of multiple metrics. Multiple display options are available.",
    "no": "Komponenten viser enkeltgrafer stablet over hverandre av flere metrikker. Ulike visningsalternativer er tilgjengelige"
  },
  "widget-description-metric-custom": {
    "en": "Widget is experimental. It displays graphs for metrics. Multiple graph options are available and configurable via script editor.",
    "no": "Denne komponenten er eksperimentell. Den viser grafer for metrikker. Flere graf alternativer er tilgjengelige og konfigurerbare via skripteditor"
  },
  "widget-description-health-single-graph": {
    "en": "Widget displays a single graph of a health metric. Multiple display options are available.",
    "no": "Komponenten viser en enkelt graf av en helse metrikk. Det er mulig å velge forskjellige grafer og tidsintervaller"
  },
  "widget-description-health-multi-graph": {
    "en": "Widget displays multiple graphs for health metrics. Multiple display options are available.",
    "no": "Komponenten viser multiple helse metrikker. Det er mulig å velge forskjellige grafer og tidsintervaller"
  },
  "widget-description-health-gauge": {
    "en": "Widget displays the newest health metric. You can set minimum, maximum and breach values.",
    "no": "Komponenten viser siste helsemetrikk. Du kan sette egne verdier for minimum, maksimum og bruddverdi."
  },
  "widget-description-asset-grid": {
    "en": "Widget displays infrastructure/asset grid. Filtering with tags and selection of visible columns are available.",
    "no": "Komponenten viser infrastruktur/ressurs. Filtrering med etiketter og valg av hvilke kolonner som skal vises er mulig"
  },
  "widget-description-asset-console": {
    "en": "Widget displays CPU, memory, disk and other information collected with system monitor for a selected asset.",
    "no": "Komponenten viser CPU, minne og disk informasjon som system monitoren samler for en valgt ressurs"
  },
  "widget-description-asset-state": {
    "en": "Widget displays the aggregated asset states for an account and subaccounts. Filtering with tags is available.",
    "no": "Komponenten viser de aggregerte ressurstilstander for en konto og subkontoer. Filtrering med etiketter er mulig"
  },
  "widget-description-sla": {
    "en": "Widget displays the current compliance state of a SLA",
    "no": "Komponenten viser gjeldende samsvarstilstand for en SLA"
  },
  "widget-description-sla-history": {
    "en": "Widget displays SLA historic periods in a bar chart format. Clicking on a bar displays more details and the root causes for the selected period.",
    "no": "Komponenten viser historiske perioder for en SLA. Klikk på en av periodene for å se helseårsaker for nedetiden"
  },
  "widget-description-sla-state": {
    "en": " Widget displays aggregated SLA states for an account and subaccounts. Filtering with tags is available.",
    "no": "Komponenten viser de aggregerte SLA tilstandende for en konto og subkontoer. Filtrering med etiketter  er tilgjengelig"
  },
  "widget-description-sla-time": {
    "en": "Widget displays current downtime, downtime before breaching compliance threshold and the total available downtime for the SLA period.",
    "no": "Komponeten viser gjeldende nedetid, nedetid før brudd på samsvarsgrensen og total mulig nedetid for SLA perioden"
  },
  "widget-description-sla-grid": {
    "en": "Widget displays SLA grid. Filtering with tags is available.",
    "no": "Komponenten viser SLA tabellen. Filter med etiketter er mulig"
  },
  "widget-description-kpi-single-graph": {
    "en": "Widget displays a single graph of a KPI metric. Multiple display options are available.",
    "no": "Komponenten viser en enkelt graf av en helse metrikk. Det er mulig å velge forskjellige grafer og tidsintervaller."
  },
  "widget-description-kpi-multi-graph": {
    "en": "Widget displays multiple graphs for selected KPI metrics. Multiple display options are available.",
    "no": "Komponenten viser multiple KPI metrikker. Det er mulig å velge forskjellige grafer og tidsintervaller."
  },
  "widget-description-kpi-gauge": {
    "en": "Widget displays the newest KPI metric. You can set minimum, maximum and breach values.",
    "no": "Komponenten viser siste KPI metrikk. Du kan sette egne verdier for minimum, maksimum og  bruddverdi."
  },
  "widget-description-kpi-history": {
    "en": " Widget displays historic KPI calculations in bar chart format. Multiple display options are available. Use display rate and period to adjust the time period for the bars.",
    "no": "Komponenten viser historiske KPI perioder. Flere visningsalternativer er tilgjengelige. Visningsrate og periode justerer tidsperioden for stolpene."
  },
  "widget-description-summary-grid": {
    "en": "Widget displays home grid. The grid contain account and service information. Filtering with tags and selection of columns to display are available.",
    "no": "Komponenten viser hjem tabellen. Den inneholder konto og tjenesteinformasjon. Filtrering med etiketter, subkontoer og valg av kolonner er tilgjengelig"
  },
  "widget-description-reasons-grid": {
    "en": "Widget displays healthreasons for assets. Filtering with tags, inclusion of subaccounts and selection of columns to display are available.",
    "no": "Komponenten viser helseårsaker. Filtrering med etiketter, subkontoer og valg av kolonner er tilgjengelig"
  },
  "widget-description-incidents-grid": {
    "en": "Widget displays incident grid. Filtering with tags and selection of columns to display are available.",
    "no": "Komponenten viser sakstabell. Filtrering med etiketter, subkontoer og valg av kolonner er tilgjengelig"
  },
  "widget-description-event-summary-grid": {
    "en": " Widget displays events for selected services. You can also filter and sort in edit mode with the common controls of the grid.",
    "no": "Komponenten viser hendelsetabellen. Filtrering med tabellfiltere kan gjøres i oppdateringsmodus"
  },
  "widget-description-text": {
    "en": " Widget displays text, hyperlinks and supports simple formatting.",
    "no": "Komponenten viser tekst, hyperlinker og støtter enkel formattering"
  },
  "widget-description-time": {
    "en": " Widget displays current time and time when dashboard last received an event.",
    "no": "Komponenten viser gjeldende tid og når dashboardet sist fikk en hendelse"
  },
  "widget-description-web-page": {
    "en": "Widget displays an embedded webpage.",
    "no": "Komponent viser en integrert nettside"
  },
  "widget-description-cloud-services-cost-bar-chart": {
    "en": " Widget displays bar chart for cloud service cost.",
    "no": "Komponenten viser kostnadene for et utvalg av skytjenester"
  },
  "widget-description-cloud-services-cost-grid": {
    "en": "Widget displays cost grid for cloud services.",
    "no": "Komponenten viser kostnadene for et utvalg av skytjenester"
  },
  "widget-description-cloud-services-instances-grid": {
    "en": "Widget displays cost grid for cloud instances",
    "no": "Komponenten viser kostnadene for et utvalg av skyinstanser"
  },
  "widget-description-cost-grid": {
    "en": " Widget displays selected items from cost model",
    "no": "Komponenten viser et utvalg fra en kostmodell"
  },
  "Hide information": {
    "no": "Skjul informasjon",
    "en": "Hide nformation"
  },
  "Search widgets": {
    "no": "Søk widgets",
    "en": "Search widgets"
  },
  "Show information": {
    "no": "Vis informasjon",
    "en": "Show information"
  },
  "widget-description-service-document": {
    "en": "Widget displays service documents. Multiple selection options are available.",
    "no": "Widgeten viser tjenestedokumenter. Flere valgmuligheter er tilgjengelig"
  },
  "widget-description-cloud-services-buggle-chart": {
    "en": "Widget displays a bubble chart for cloud services cost",
    "no": "Komponenten viser kostnadene for skytjenester"
  },
  "widget-description-budget-cost-bar-chart": {
    "en": "Widget displays bar chart for cost model.",
    "no": "Komponenten viser et utvalg fra en kostmodell."
  },
  "widget-description-gps": {
    "en": "Widget displays a map with asset or service shapes that can be moved with GPS positions.",
    "no": "Widget viser et kart med ressurs eller tjeneste former som kan flyttes med GPS posisjoner.."
  },
  "widget-description-cost-profile": {
    "en": "Widget displays selected cost model.",
    "no": "Viser en kostmodell"
  },
  "widget-description-metric-sparkline-table": {
    "en": "Widget displays sparkline metric table with unit, last Metric sample time, mean value and graph for the last hour.",
    "no": "Sparkline viser metrikk i tabell med enhet, siste metrikktid, verdi og graf for den siste timen."
  },
  "widget-description-event-state": {
    "en": "Widget displays event states for an account. Filtering with tags and severity is available.",
    "no": "Widget viser hendelse tilstand for konto. Filtering med etiketter og alvorlighet er tilgjengelig."
  },
  "widget-description-service-events": {
    "en": "Widget display events for selected services. You can also filter and sort when in edit mode with the common controls of the grid.",
    "no": " Widget viser hendelser for de valgte tjenestene. Du kan filtrer og sorter også når du er i redigeringsmodus med de vanlige kontrollene i tabellen."
  },
  "widget-description-data-sources-grid": {
	  en: 'Widget displays datasource grid which uses a datamatrix datasource to build it’s content.',
	  no: 'Datakilde tabell bruker en datamatrix datakilde for å generere tabellinnhold.'
  },
	"widget-description-service-summary-grid": {
		en: 'Widget displays summary grid with states for any servicemodel, element and/or qualifier. You can control filtering and view settings.',
		no: 'Sammendragstabell viser tilstand for utvalgte tjeneste, tjeneste element og/eller tjenestepunkt. I tabellen kan du endre filtre og kolonne oppsett.'
	},
	"widget-description-ai-query": {
	  en: "Widget displays AI reports with selected metrics or cost values using AI query.",
		no: "Lag AI-rapporter ved å velge metrikker eller kostnadsverdier fra dashbordet,"
	},
	"widget-description-report": {
	  en: 'Widget allows to generate a selected report on click. You can define time period for data generated.',
		no: 'Lager en rapport på klikk. Du kan velge tidsperiod og hvordan rapporten blir presentert.'
	},
	"widget-description-data-sources-chart": {
	  en: 'This chart provides various visual representations of data sources, making it an excellent tool for comparing and analyzing data effectively.',
	  no: 'Dette diagrammet gir ulike visuelle representasjoner av datakilder, noe som gjør det til et utmerket verktøy for å sammenligne og analysere data effektivt.'
	},
	"widget-description-indicator-dots": {
		en: 'Indicator dots display a range of colored dots depending on the value of the selected metric.',
		no: 'Indikatorpunkter viser et utvalg fargede prikker avhengig av verdien til den valgte metrikken.'
  },
	"widget-description-data-sources-single-graph": {
	  en: 'Widget displays a single line, area or range graph for a datasource, plotting a chosen metric column against a chosen time column.',
	  no: 'Widget viser en enkel linje-, område- eller intervallgraf for en datakilde, basert på en valgt metrikkolonne og en valgt tidskolonne.'
	},
	"widget-description-asset-assetgroup": {
		en: 'The Assetgroup widget lets you display assets from a selected assetgroup in your dashboard.<br/>It shows each asset along with its severity level, helping you quickly identify critical issues.<br/>You can:<br/>Sort and filter the assets.<br/>Customize how the assets are visually presented.<br/>This makes it easier to focus on the most important information at a glance.<br/>',
		no: 'Ressursgruppe widget lar deg vise ressurser fra en valgt ressursgruppe i dashboardet.<br/>Hver ressurs vises sammen med alvorlighetsgrad, slik at du raskt kan identifisere kritiske problemer.<br/>Du kan:<br/>Sortere og filtrere ressurser.<br/>Tilpasse hvordan ressurser vises visuelt.<br/>Dette gjør det enklere å fokusere på den viktigste informasjonen med ett blikk.<br/>'
	},
	"widget-description-metric-tile": {
		en: 'Metric Tile display a single metric value in a compact tile format. It supports an optional color or icon indicator that dynamically changes based on the metric value.',
		no: 'Metrikk panel viser en enkelt metrikkverdi i et kompakt panelformat. Den støtter en valgfri farge- eller ikonindikator som endres dynamisk basert på metrikken.'
	},
	"widget-description-metric-compas": {
		en: 'A circular gauge widget that visualizes data on a 0°–360° scale.',
		no: 'En sirkulær måler som visualiserer data på en 0°–360° skala.'
	},
	"widget-description-data-sources-tile": {
		en: 'The Datasource Tile displays values or text taken from a specific row in the selected datasource. It allows you to display key information from any column within that row directly on the dashboard.<br/><br/>Row Selection Requirements: The row used by the tile must be uniquely identifiable. To ensure consistent and correct data lookup, the selection should be based on a unique column—such as a row header, index, or another field that contains unique values.<br/><br/>Column Selection: Once a row is selected, any column from that row may be chosen display settings. ',
		no: 'Datakildeflisen viser verdier eller tekst hentet fra en bestemt rad i den valgte datakilden. Den gjør det mulig å vise informasjon fra hvilken som helst kolonne i denne raden direkte på dashbordet.<br/><br/>Krav til radvalg: Raden som brukes av flisen må kunne identifiseres entydig. For å sikre korrekt og konsistent datatilgang bør valget baseres på en unik kolonne – for eksempel en radheader, indeks eller et annet felt som inneholder unike verdier.<br/><br/>Kolonnevalg: Når en rad er valgt, kan hvilken som helst kolonne fra denne raden velges i visningsinnstillingene.'
	},
	"widget-description-global-time-selector": {
		en: 'The Datasource Time Selector allows you to control the time range for your entire dashboard from one place.<br/><br/>This timebar acts as a master switch. When you pick a time range here, it automatically syncs time in:<br/><br/>Datasource/Grid<br/>Datasource/Chart<br/><br/>Save time by changing your view once rather than updating multiple individual widgets.',
		no: 'Tidsvelgeren lar deg kontrollere tidsperioden for hele dashbordet ditt fra ett sted.<br/><br/>Denne tidslinjen fungerer som en hovedbryter. Når du velger en tidsperiode her, synkroniseres tiden automatisk i alle:<br/><br/>Datakilde/Tabell<br/>Datakilde/Diagram<br/><br/>Spar tid ved å endre visningen en gang, i stedet for å oppdatere hver enkelt widget manuelt.'
	},
	"widget-description-metric-compass": {
		en: 'A circular gauge widget that visualizes data on a 0°–360° scale.',
		no: 'En sirkulær måler som visualiserer data på en 0°–360° skala.'
	},
	"widget-description-metric-dial": {
		en: 'A circular gauge widget that visualizes data on a 0°–360° scale.',
		no: 'En sirkulær måler som visualiserer data på en 0°–360° skala.'
	},
	"widget-description-export": {
		en: 'The Export widget lets you export data from one or more datasource widgets on this dashboard.<br/><br/>Click the icon on the canvas to pick the widgets you want and choose an export format (CSV, Excel or JSON), then download the result.',
		no: 'Eksporter-widgeten lar deg eksportere data fra en eller flere datakilde-widgets på dette dashbordet.<br/><br/>Klikk på ikonet på lerretet for å velge widgetene du vil eksportere og velg et eksportformat (CSV, Excel eller JSON), og last deretter ned resultatet.'
	}
});

export const WidgetForm = observer(class WidgetForm extends React.Component{
	state = {}
	legacyFormRef = React.createRef();

	constructor(props){
		super(props);
	}


	render() {
		const store = this.props.store

		toJS(store.widgetConfig)

		const link = linkObservable(this, store.widgetConfig)
		let form = null;

		if (this.isLegacyForm()) {
			form = <LegacyWidgetForm ref={this.legacyFormRef}
			                         store={store}/>
		} else {
			const ReactForm = store.widgetDescription.form
			form = <ReactForm designerStore={store.designerStore}
			                  configLink={link}
			                  widgetDescription={store.widgetDescription}
			                  config={store.widgetConfig}
			                  setValidator={store.setFormValidator}
			                  setFormConfigUpdater={store.setFormConfigUpdater}
			                  mode={store.mode} />
		}

		return <>
			{this.state.validationMessage && <ValidationSummary message={this.state.validationMessage} type="error" />}
			{form}
			<RedirectConfigEditor config={store.redirectConfig}/>
		</>
	}

	isValid() {
		let isValid = false, validationMessage = '';
		if(this.isLegacyForm()){
			validationMessage = this.legacyFormRef.current.isValid().validationMessage;
			isValid = this.legacyFormRef.current.isValid().valid;
		} else if (this.props.store.formValidator) {
			this.props.store.formValidator.validate();
			validationMessage = this.props.store.formValidator.errors.map(eo => eo.errors.join(', ') + ': ' + eo.field).join('; ');
			isValid = this.props.store.formValidator.valid;
		} else {
			const link = linkObservable(this, this.props.store.widgetConfig);
			validationMessage = link.errorSubtree?.join(', ');
			isValid = !link.invalidSubtree;
		}

		this.setState({
			validationMessage
		});
		return isValid;
	}

	reset(){
		this.setState({
			validationMessage: null
		})
	}

	isLegacyForm(){
		return this.props.store.widgetDescription.legacyForm != null;
	}

	getConfig() {
		if(!this.isLegacyForm()) {
			this.props.store.formConfigUpdater?.();
			return null
		}

		const config = this.legacyFormRef.current.getConfig();

		//legacy form hardcoded types are not always the same as the current ones
		config.type = this.props.store.widgetConfig.type;

		return config
	}
})

class LegacyWidgetForm extends React.Component{
	containerRef = React.createRef();
	mobx = new MobxManager()

	render() {
		return (
			<Section appearance={"none"}
			         contentPadding={false}
			         childrenPadding={true}>
				<div ref={this.containerRef}></div>
			</Section>
		)
	}

	componentWillUnmount() {
		this.mobx.destroy()
		this.formInstance?.destroy && this.formInstance.destroy()
	}

	componentDidMount() {
		this.mobx.reaction(() => this.props.store.widgetConfig.type, x => {
			if(this.props.store.widgetDescription.legacyForm) {
				setTimeout(() =>this.reloadForm(), 0)
			}
		}, {
			fireImmediately: true
		})
	}

	reloadForm(){
		this.containerRef.current.innerHTML = '';

		this.formInstance?.destroy?.()

		this.formInstance = new this.props.store.widgetDescription.form({
			serviceBoardAccountId: ApplicationState.accountId,
			widgetObj: this.props.store.widgetConfig,
			widgetConfigurationFormEl: $(this.containerRef.current),
			mode: this.props.store.mode,
			wasUpdate: this.props.store.mode != 'create',
			widget: this.props.store.widgetDescription.widget
		});
	}

	isValid(){
		return this.formInstance.isValid();
	}

	getConfig(){
		return this.formInstance.getValues();
	}
}

const b = require('b_').with('widgets-wizard');

export const WidgetsWizard = observer(props => {
	const store = props.store

	const widgetFormRef = React.useRef()

	const onOk = () => {
		const form = widgetFormRef.current;
		if(!form.isValid() || !store.redirectConfig.isValid)
			return;

		//let cell = store.cell //cell will be cleared in the end of saveWidget

		store.saveWidget(form.getConfig())

		const graph = store.designerStore.legacyDesigner.graph

		graph.setCellStyles(
			'forceInvalidate',
			newGuid(),
			[store.cell]
		);

		store.closeWizard()
	}

	const onCancel = () => {
		store.closeWizard()
	}

	const onSelect = async item =>{
		store.changeType(item.key)
		widgetFormRef.current.reset();
	}

	const [height, top] = useMemo(() => {
		const designerContainer = store.designerStore.config.container;
		const rect = designerContainer.getBoundingClientRect();
		return [rect.height - 40, rect.y + 20];
	}, [store]);

	const [openKeys, onOpenChange] = useMenuNavigation(props);

	const [filterText, setFilterText] = React.useState('');

	const menuItems = useMemo(() => {
		const items = generateMenuItems(false, true);
		if (!filterText)
			return items;

		const lowerFilterText = filterText.toLocaleLowerCase();
		return items
			.map(section => ({
				...section,
				children: section.children.filter(widget => widget.label.toLocaleLowerCase().includes(lowerFilterText))
			}))
			.filter(section => section.children.length > 0);
	}, [filterText]);

	let [preview, showPreview, hidePreview] = useExpandCollapsePreview();

	return (
		<AntModal open={true}
			mask={{closable: false}}
			height={height}
			mode={store.mode}
			onOk={onOk}
			onCancel={onCancel}
			width={store.width}
			style={{top: top}}
		    minWidth={900}
		>
			<Section direction={"row"}
				contentPadding={true}
				containerClass={b()}
				appearance={"none"}
				childrenPadding={true}
				scrollable={'y'}
			>
				<Toolbar title={store.title}>
					{preview
						? <AntButton icon={<CaretRightOutlined/>} type={"text"} size={"small"} onClick={hidePreview} />
						: <AntButton icon={<CaretLeftOutlined />} type={"text"} size={"small"} onClick={showPreview} />
					}
				</Toolbar>
				{store.mode == 'create' && !store.hideWidgetSelector &&
					<Section containerClass={b('menu')}
					         appearance={"none"}
					         scrollable={true}>
						<AntInput className={b('filter')}
						          value={filterText}
						          onChange={setFilterText}
						          placeholder={i18n('Search widgets')}
						          suffix={<SearchOutlined/>}/>
						<Menu mode="inline"
						      onSelect={onSelect}
						      defaultSelectedKeys={[store.widgetConfig.type]}
						      openKeys={filterText ? menuItems.map(section => section.key) : openKeys}
						      onOpenChange={onOpenChange}
						      inlineIndent={25}
						      style={{ width: 256 }}
							  items={menuItems}>
						</Menu>
					</Section>
				}
				<Section containerClass={b('form')}
				         appearance={"none"}>
					<WidgetForm store={store}
					            ref={widgetFormRef}/>
				</Section>
				{!preview && <Section appearance={"none"}
				                      containerClass={b('preview-container')}
				                      childrenPadding={true}>
					<div className={b('preview-container')}>
						<img className={b('preview')}
						     src={'assets-static/dashboard-designer/previews/' + store.widgetConfig.type + '.png'}
					         alt={'Preview'}/>
					</div>
					<p dangerouslySetInnerHTML={{__html: i18n('widget-description-' + store.widgetConfig.type)}}></p>
				</Section>
				}
			</Section>
		</AntModal>
	)
})

function useExpandCollapsePreview(){
	const [preview, setPreview] = React.useState(true);

	let updateUserSettings = async function(value){
		setPreview(value)
		await updateDesignerSetting('preview', value);
	}

	const hidePreview = React.useCallback(() => {
		updateUserSettings(false)
	}, []);

	const showPreview = React.useCallback(() => {
		updateUserSettings(true);
	}, []);

	React.useEffect(() => {
		var body = async function(){
			var settings = await loadDesignerSettings();
			setPreview(settings.previewCollapsed);
		}

		body();
	}, [])

	return [preview, showPreview, hidePreview];
}

function useMenuNavigation(props){
	const defaultOpenKeys = React.useMemo(() => {
		return [getWidgetsMenuStructure()
			.find( section => section.widgets.find(widget => widget.type == props.store.widgetConfig.type))?.title
		]
	},[])

	const [openKeys, setOpenKeys] = React.useState(defaultOpenKeys);

	const onOpenChange = useCallback( newOpenKeys => {
		const latestOpenKey = newOpenKeys.find(key => openKeys.indexOf(key) === -1);
		if (!getWidgetsMenuStructure().find(section => section.title == latestOpenKey)) {
			setOpenKeys( newOpenKeys );
		} else {
			setOpenKeys( latestOpenKey ? [latestOpenKey] : []);
		}
	}, [openKeys]);


	return [openKeys, onOpenChange];
}
